import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { tap, catchError, switchMap, first, map, finalize } from 'rxjs/operators';
import { of, Observable, forkJoin } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LearningService } from '@shared/services/api/learning.service';
import { LearningFormService } from '@shared/services/api/learning-form.service';
import { Learning } from '@shared/entities/learning';
import { ThemeService } from 'src/app/services/theme.service';
import { ValuesService } from '@shared/services/api/values.service';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { LearningComplexityService } from '@shared/services/api/learning-complexity.service';
import { Language } from '@shared/entities/language';
import { LanguageService } from '@shared/services';

interface MachineTypologyOption {
  name: string;
  id: string;
}

interface ComplexityOption {
  id: string;
  label: string;
}

interface LanguageOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-learning-form',
  templateUrl: './learning-form.component.html',
  styleUrl: './learning-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LearningFormComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private _formBuilder = inject(FormBuilder);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _learningFormService = inject(LearningFormService);
  private _learningService = inject(LearningService);
  private _learningComplexityService = inject(LearningComplexityService);
  private _themeService = inject(ThemeService);
  private _location = inject(Location);
  public translateService = inject(TranslateService);
  private _valuesService = inject(ValuesService);
  private _languageService = inject(LanguageService);

  form!: FormGroup;
  private _learning: Learning = new Learning();
  _learningId = '';
  isEdit = false;
  isLight = true;
  isLoading = false;
  errorMessage = '';

  showSuccess = false;
  showError = false;
  saveSuccess = false;
  saveError = false;
  deleteSuccess = false;
  deleteError = false;

  previewAttachment: File | null = null;
  existingPreviewAttachment: any = null;

  videoPaths: string[] = [];
  imagePaths: string[] = [];
  documentPaths: string[] = [];

  machineTypologies: string[] = [];
  typologyOptions: MachineTypologyOption[] = [];

  complexityLevels: string[] = [];
  complexityOptions: ComplexityOption[] = [];
  complexityField: any = { key: 'complexity', values: [] };
  @ViewChild('complexitySelect', { static: false }) complexitySelect: any;

  languages: Language[] = [];
  languageOptions: LanguageOption[] = [];
  languageField: any = { key: 'language', values: [] };
  @ViewChild('languageSelect', { static: false }) languageSelect: any;

  // ... rest of the properties remain the same ...
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '300px',
    maxHeight: '300px',
    width: 'auto',
    minWidth: '0',
    translate: 'no',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter text here...',
    defaultParagraphSeparator: '',
    defaultFontName: '',
    defaultFontSize: '',
    sanitize: true,
    toolbarPosition: 'top',
    toolbarHiddenButtons: [
      ['fontName', 'subscript', 'superscript'],
      ['insertImage', 'textColor', 'backgroundColor', 'insertVideo']
    ],
    customClasses: []
  };

  constructor() {
    /* noop */
  }

  ngOnInit(): void {
    this.initializeForm();
    this.isLoading = true;

    forkJoin({
      theme: this._themeService.isLight$.pipe(first()),
      typologies: this.loadMachineTypologiesData(),
      complexities: this.loadComplexityLevels(),
      languages: this.loadLanguages()
    })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          if (!this.isEdit) {
            this.isLoading = false;
            this._cdr.markForCheck();
          }
        })
      )
      .subscribe({
        next: (results) => {
          this.isLight = results.theme;
          this._cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'Failed to load form data';
          this.saveError = true;
          this._cdr.markForCheck();
        }
      });

    this._route.params
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          this._learningId = params['id'] || '';
          this.isEdit = !!this._learningId;
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    if (this.isEdit && this._learningId) {
      this.loadLearningData();
    }
  }

  private initializeForm(): void {
    this.form = this._formBuilder.group({
      title: ['', Validators.required],
      content: ['', Validators.required],
      complexity: ['', Validators.required],
      machineTypes: [[], Validators.required],
      preview: [undefined, Validators.required],
      tags: [[]],
      language: ['', Validators.required]
    });
  }

  private loadComplexityLevels(): Observable<boolean> {
    return this._learningComplexityService.getComplexities().pipe(
      first(),
      map((response) => {
        if (response) {
          this.complexityLevels = response.complexities || [];
          this.complexityOptions = this.complexityLevels.map((complexity) => ({
            id: complexity,
            label: this.translateService.instant(`COMMON.COMPLEXITY.${complexity.toUpperCase()}`) || complexity
          }));
          this.complexityField = {
            key: 'complexity',
            values: this.complexityOptions.map((opt) => ({ id: opt.id, value: opt.label }))
          };
          return true;
        }
        return false;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  private loadLanguages(): Observable<boolean> {
    return this._languageService.getLanguages().pipe(
      first(),
      map((response) => {
        if (response && response.languages) {
          this.languages = response.languages;
          this.languageOptions = this.languages.map((language) => ({
            id: language.lang || '',
            label: language.lang || ''
          }));
          this.languageField = {
            key: 'language',
            values: this.languageOptions.map((opt) => ({ id: opt.id, value: opt.label }))
          };

          if (!this.isEdit && this.languageOptions.length > 0) {
            this.form.get('language')?.setValue(this.languageOptions[0].id);
          }

          return true;
        }
        return false;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  private loadMachineTypologiesData(): Observable<boolean> {
    return this._valuesService.getMachineTypes().pipe(
      first(),
      map((response) => {
        const values = response.values;
        if (Array.isArray(values)) {
          this.machineTypologies = values;
          this.typologyOptions = this.machineTypologies.map((typology) => ({
            name: typology,
            id: typology
          }));
        }
        return true;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  fetchTypologies = (searchTerm: string): Observable<MachineTypologyOption[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return of(this.typologyOptions);
    }

    const filteredOptions = this.typologyOptions.filter((option) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return of(filteredOptions);
  };

  private loadLearningData(): void {
    if (!this._learningId) {
      this.isLoading = false;
      this._cdr.markForCheck();
      return;
    }

    this.isLoading = true;

    this._learningFormService
      .findByFilter(this._learningId)
      .pipe(
        switchMap((learning) => (learning ? of(learning) : this._learningFormService.findLearning(this._learningId))),
        takeUntilDestroyed(this._destroyRef),
        tap((learning) => {
          if (learning) {
            this._learning = learning;
            this.patchFormValues(learning);
          }
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
        catchError(() => {
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  private patchFormValues(learning: Learning): void {
    let machineTypologyOptions: MachineTypologyOption[] = [];
    if (learning.machineTypes && Array.isArray(learning.machineTypes)) {
      machineTypologyOptions = learning.machineTypes.map((typology) => {
        const option = this.typologyOptions.find((opt) => opt.id === typology);
        return option || { id: typology, name: typology };
      });
    }

    const formValues = {
      title: learning.title || '',
      content: learning.content || '',
      complexity: learning.complexity || '',
      machineTypes: machineTypologyOptions,
      tags: learning.tags || [],
      language: learning.language || ''
    };

    this.form.patchValue(formValues);

    if (learning.complexity) {
      setTimeout(() => {
        const comp: any = (this as any).complexitySelect;
        if (comp && typeof comp.setSelectedValue === 'function') {
          comp.setSelectedValue(learning.complexity);
        }
      }, 0);
    }

    if (learning.language) {
      setTimeout(() => {
        const lang: any = (this as any).languageSelect;
        if (lang && typeof lang.setSelectedValue === 'function') {
          lang.setSelectedValue(learning.language);
        }
      }, 0);
    }

    this.existingPreviewAttachment = learning.preview || null;
    if (this.existingPreviewAttachment) {
      this.form.get('preview')?.setValue(this.existingPreviewAttachment);
      this.form.get('preview')?.setValidators(null);
      this.form.get('preview')?.updateValueAndValidity();
    } else {
      this.form.get('preview')?.setValue(undefined);
    }

    this.videoPaths = learning.videoPaths || [];
    this.imagePaths = learning.imagePaths || [];
    this.documentPaths = learning.documentPaths || [];
  }

  getYear() {
    return new Date().getFullYear();
  }

  isAttachmentsValid(): boolean {
    return !!(this.previewAttachment || this.existingPreviewAttachment);
  }

  onPreviewFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.previewAttachment = input.files[0];
      this.form.get('preview')?.setErrors(null);
    }
  }

  onFileInputSelected(fileOrFiles: File | File[] | null): void {
    if (!fileOrFiles) {
      return;
    }

    if (Array.isArray(fileOrFiles)) {
      if (fileOrFiles.length > 0) {
        this.previewAttachment = fileOrFiles[0];
        this.form.get('preview')?.setErrors(null);
      }
    } else {
      this.previewAttachment = fileOrFiles;
      this.form.get('preview')?.setErrors(null);
    }
  }

  onVideoPathsChanged(paths: string[]): void {
    this.videoPaths = paths;
  }

  onImagePathsChanged(paths: string[]): void {
    this.imagePaths = paths;
  }

  onDocumentPathsChanged(paths: string[]): void {
    this.documentPaths = paths;
  }

  removePreviewAttachment(): void {
    if (this.previewAttachment) {
      this.previewAttachment = null;
      this.form.get('preview')?.setValue(undefined);
    } else {
      this.existingPreviewAttachment = null;
    }

    this.form.get('preview')?.setValidators([Validators.required]);
    this.form.get('preview')?.markAsDirty();
    this.form.get('preview')?.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.form.invalid || !this.isAttachmentsValid()) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });

      if (!this.isAttachmentsValid()) {
        this.form.get('preview')?.markAsTouched();
      }
      return;
    }

    this.isLoading = true;
    this.resetStateFlags();

    const learningData = { ...this.form.value };

    if (learningData.machineTypes && Array.isArray(learningData.machineTypes)) {
      learningData.machineTypes = learningData.machineTypes.map((item: any) => item.id || item);
    }

    delete learningData.preview;

    learningData.videoPaths = this.videoPaths;
    learningData.imagePaths = this.imagePaths;
    learningData.documentPaths = this.documentPaths;

    const hasNewPreview = !!this.previewAttachment;

    const request = hasNewPreview
      ? this.isEdit
        ? this._learningFormService.updateLearningWithAttachments(this._learningId, learningData, {
            preview: this.previewAttachment!
          })
        : this._learningFormService.saveLearningWithAttachments(learningData, { preview: this.previewAttachment! })
      : this.isEdit
        ? this._learningFormService.editLearning(learningData, this._learningId)
        : this._learningFormService.saveLearning(learningData);

    request
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response && (response instanceof Error || response.error)) {
            this.showError = true;
            this.saveError = true;
            this.saveSuccess = false;
            this.errorMessage = this.extractErrorMessage(response);
            this._cdr.markForCheck();
            return;
          }

          this.showSuccess = true;
          this.saveSuccess = true;
          this.saveError = false;
          this._learningService.refreshFilters();
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          this.isLoading = false;
          this.showError = true;
          this.saveError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  toggleLearningActivation(): void {
    if (!this._learningId || !this._learning) {
      return;
    }

    this.isLoading = true;
    this.resetStateFlags();

    const updatedLearning: any = {
      title: this._learning.title || '',
      active: this._learning.active !== undefined ? !this._learning.active : true
    };

    this._learningFormService
      .editLearning(updatedLearning, this._learningId)
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((response: any) => {
          this.isLoading = false;

          if (response && (response instanceof Error || response.error)) {
            this.showError = true;
            this.deleteError = true;
            this.errorMessage = this.extractErrorMessage(response);
            this._cdr.markForCheck();
            return;
          }

          this.showSuccess = true;
          this.deleteSuccess = true;
          this._learningService.refreshFilters();
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          this.isLoading = false;
          this.showError = true;
          this.deleteError = true;
          this.errorMessage = this.extractErrorMessage(error?.error || error);
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  isLearningActive(): boolean {
    return this._learning?.active !== false;
  }

  getToggleButtonText(): string {
    return this.isLearningActive() ? 'LEARNING_FORM.DEACTIVATE_BUTTON' : 'LEARNING_FORM.ACTIVATE_BUTTON';
  }

  getToggleSuccessTitle(): string {
    return this.isLearningActive() ? 'LEARNING_FORM.DEACTIVATE_SUCCESS' : 'LEARNING_FORM.ACTIVATE_SUCCESS';
  }

  getToggleSuccessDescription(): string {
    return this.isLearningActive()
      ? 'LEARNING_FORM.DEACTIVATED_LEARNING_DESCRIPTION'
      : 'LEARNING_FORM.ACTIVATED_LEARNING_DESCRIPTION';
  }

  getToggleErrorTitle(): string {
    return this.isLearningActive() ? 'LEARNING_FORM.DEACTIVATE_ERROR_TITLE' : 'LEARNING_FORM.ACTIVATE_ERROR_TITLE';
  }

  getToggleErrorDescription(): string {
    return this.isLearningActive()
      ? 'LEARNING_FORM.DEACTIVATE_ERROR_DESCRIPTION'
      : 'LEARNING_FORM.ACTIVATE_ERROR_DESCRIPTION';
  }

  goToList(): void {
    this._router.navigate(['/learning']);
  }

  goBack(): void {
    this._location.back();
  }

  tryAgain(): void {
    this.saveError = false;
    this.deleteError = false;
    this.showError = false;
  }

  private resetStateFlags(): void {
    this.showSuccess = false;
    this.showError = false;
    this.saveSuccess = false;
    this.saveError = false;
    this.deleteSuccess = false;
    this.deleteError = false;
    this.errorMessage = '';
  }

  onComplexitySelected(event: any): void {
    if (!event) return;

    const value = event.selectedValue;
    if (value !== undefined) {
      this.form.get('complexity')?.setValue(value);
      this.form.get('complexity')?.markAsDirty();
      this.form.get('complexity')?.markAsTouched();
    }
  }

  onLanguageSelected(event: any): void {
    if (!event) return;

    const value = event.selectedValue;
    if (value !== undefined) {
      this.form.get('language')?.setValue(value);
      this.form.get('language')?.markAsDirty();
      this.form.get('language')?.markAsTouched();
    }
  }

  private extractErrorMessage(response: any): string {
    if (!response) {
      return this.translateService.instant('LEARNING_FORM.ERROR_UNKNOWN');
    }

    if (response.message) {
      return response.message;
    }

    if (response.validation && response.validation.fields && response.validation.fields.length > 0) {
      const validationErrors = response.validation.fields
        .map((field: any) => `${field.field}: ${field.error}`)
        .join('\n');

      return `Validation errors:\n${validationErrors}`;
    }

    if (response.error && typeof response.error === 'string') {
      return response.error;
    }

    if (this.saveError) {
      return this.translateService.instant('LEARNING_FORM.ERROR_SAVING');
    } else if (this.deleteError) {
      return this.translateService.instant('LEARNING_FORM.ERROR_DELETING');
    }

    return this.translateService.instant('LEARNING_FORM.ERROR_UNKNOWN');
  }
}
